/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Side effects for the ViewAppointments page hook.
 * @module pages/viewAppointments/useViewAppointmentsEffects
 */

import { useEffect } from "react";
import { SEARCH_DEBOUNCE_MS } from "./viewAppointmentsConstants.js";
import { scheduleSuccessMessageHide } from "./viewAppointmentsSuccessMessage.js";
import styles from "../../styles/ViewAppointments.module.css";
import useViewAppointmentsUserContextEffect from "./useViewAppointmentsUserContextEffect.js";

/**
 * Registers debounce, list reload, calendar sync, and click-outside effects.
 * @param {Object} ctx - ViewAppointments context
 * @param {Object} fetching - Fetch helpers from `useViewAppointmentsFetching`
 */
const useViewAppointmentsEffects = (ctx, fetching) => {
  const {
    location,
    eventData,
    userData,
    searchQuery,
    setDebouncedSearch,
    setEventData,
    setShowSuccessMessage,
    setIsSlidingUp,
    setFilteredRequests,
    setTotalAppointments,
    setCurrentPage,
    setAppointments,
    setAllAppointmentsForConflict,
    setSenderAppointments,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    selectedRequirement,
    debouncedSearch,
    selectedStatusFilter,
    selectedRequirementsFilter,
    resultsLimit,
    activeDropdownId,
    isFiltersDropdownOpen,
    setActiveDropdownId,
    setIsFiltersDropdownOpen,
    appointmentsPageRequestRef,
  } = ctx;

  const { canLoadAppointmentsForUser, fetchAppointmentsPage, fetchCalendarAppointments } =
    fetching;

  useViewAppointmentsUserContextEffect(ctx);

  useEffect(() => {
    if (location.state?.event) {
      setEventData(location.state.event);
    }

    if (location.state?.showScheduleSuccess) {
      setShowSuccessMessage(true);
      scheduleSuccessMessageHide({
        setSlidingUp: setIsSlidingUp,
        setVisible: setShowSuccessMessage,
      });
    }
  }, [location.state, setEventData, setShowSuccessMessage, setIsSlidingUp]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, setDebouncedSearch]);

  useEffect(() => {
    if (!eventData?._id || !userData) {
      return;
    }
    if (!canLoadAppointmentsForUser()) {
      appointmentsPageRequestRef.current += 1;
      setFilteredRequests([]);
      setTotalAppointments(0);
      setCurrentPage(1);
      return;
    }
    fetchAppointmentsPage(1);
  }, [
    eventData,
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    selectedRequirement,
    debouncedSearch,
    selectedStatusFilter,
    selectedRequirementsFilter,
    resultsLimit,
    canLoadAppointmentsForUser,
    fetchAppointmentsPage,
    appointmentsPageRequestRef,
    setFilteredRequests,
    setTotalAppointments,
    setCurrentPage,
  ]);

  useEffect(() => {
    if (!eventData?._id || !userData) {
      return;
    }
    if (!canLoadAppointmentsForUser()) {
      setAppointments([]);
      setAllAppointmentsForConflict([]);
      setSenderAppointments([]);
      return;
    }
    fetchCalendarAppointments();
  }, [
    eventData,
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    canLoadAppointmentsForUser,
    fetchCalendarAppointments,
    setAppointments,
    setAllAppointmentsForConflict,
    setSenderAppointments,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeDropdownId &&
        !event.target.closest(`.${styles.actionsDropdownButton}`) &&
        !event.target.closest(`.${styles.actionsDropdownMenu}`)
      ) {
        setActiveDropdownId(null);
      }

      if (
        isFiltersDropdownOpen &&
        !event.target.closest(`.${styles.settingsButton}`) &&
        !event.target.closest(`.${styles.filtersDropdownMenu}`)
      ) {
        setIsFiltersDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    activeDropdownId,
    isFiltersDropdownOpen,
    setActiveDropdownId,
    setIsFiltersDropdownOpen,
  ]);
};

export default useViewAppointmentsEffects;
